import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useRates } from "@/context/RateContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Download, Tag, ImagePlus, Loader2, Sparkles, FileText, Lock, Unlock } from "lucide-react";
import { sanitizeDecimal } from "@/lib/validators";
import { tokenStorage } from "@/lib/tokenStorage";

const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

/**
 * Türkçe sayı formatı ayrıştırıcı — kuyumcuların doğal yazımını kabul eder:
 *   "10.000"    → 10000     (Türkçe binlik ayırıcı)
 *   "10.500,50" → 10500.50  (Türkçe binlik + ondalık)
 *   "10,5"      → 10.5      (Türkçe ondalık)
 *   "0,916"     → 0.916     (Türkçe ondalık ayar)
 *   "10.5"      → 10.5      (JS-native — kullanıcılar her iki formatı da girer)
 *   "0.916"     → 0.916     (JS-native)
 *   "1.234"     → 1234      (Türkçe binlik — 3 hane sonrası + 0 ile başlamıyor)
 * Yanlış / boş → { valid: false }
 */
export function parseTrNumber(raw) {
  if (raw == null) return { value: NaN, valid: false };
  if (typeof raw === "number") return { value: raw, valid: Number.isFinite(raw) };
  const s = String(raw).trim().replace(/\s+/g, "");
  if (s === "") return { value: NaN, valid: false };
  if (!/^-?[\d.,]+$/.test(s)) return { value: NaN, valid: false };

  const commas = (s.match(/,/g) || []).length;
  const dots = (s.match(/\./g) || []).length;
  if (commas > 1) return { value: NaN, valid: false };

  let normalized;
  if (commas === 1) {
    // Virgül varsa: virgül ondalık, noktalar binlik ayırıcı.
    normalized = s.replace(/\./g, "").replace(",", ".");
  } else if (dots >= 2) {
    // Birden fazla nokta → binlik ayırıcı.
    normalized = s.replace(/\./g, "");
  } else if (dots === 1) {
    // Tek nokta — muğlak. Sağı tam 3 hane VE solu 0'la başlamıyorsa → binlik.
    const [left, right] = s.split(".");
    const looksLikeThousands =
      right.length === 3 && left.length >= 1 && !/^0/.test(left);
    normalized = looksLikeThousands ? left + right : s;
  } else {
    normalized = s;
  }

  const n = parseFloat(normalized);
  return { value: n, valid: Number.isFinite(n) };
}

// Harem coin fallback chain — TAM_YENI ve CUMHURIYET Harem feed'inde her zaman olmayabilir;
// benzer gramaj/milyemli varyanta düşerek dükkan sahibi hiç yanlış fiyat görmesin.
// Object bazlı: {code, mult?} — mult=5 ise 5×Ata (Ata Beşli), mult=2.5 ise İkibuçuk vb.
const HAREM_FALLBACK = {
  TAM_YENI:      [{ code: "ATA_YENI" }],
  TAM_ESKI:      [{ code: "TAM_YENI" }, { code: "ATA_YENI" }],
  CUMHURIYET:    [{ code: "ATA_YENI" }, { code: "TAM_YENI" }],
  CUMHURIYET_ESKI: [{ code: "CUMHURIYET" }, { code: "ATA_YENI" }],
  CEYREK_ESKI:   [{ code: "CEYREK_YENI" }],
  YARIM_ESKI:    [{ code: "YARIM_YENI" }],
  ATA_ESKI:      [{ code: "ATA_YENI" }],
  RESAT_YENI:    [{ code: "ATA_YENI" }],
  RESAT_ESKI:    [{ code: "ATA_YENI" }],
  HAMIT_YENI:    [{ code: "ATA_YENI" }],
  HAMIT_ESKI:    [{ code: "ATA_YENI" }],
  GREMESE_ESKI:  [{ code: "GREMESE_YENI" }],
  BESLIYENI:     [{ code: "ATA_YENI", mult: 5 }],
  BESLIESKI:     [{ code: "ATA_YENI", mult: 5 }],
  IKIBUCUKYENI:  [{ code: "ATA_YENI", mult: 2.5 }],
  IKIBUCUKESKI:  [{ code: "ATA_YENI", mult: 2.5 }],
};

// Gram-bazlı Harem kodları: fiyat = karat_gram_price × net_weight
// (Bilezikler, düz gram ürünleri, has gümüş için).
export const GRAM_BASIS_HAREM_CODES = new Set([
  "ALTIN", "AYAR22", "AYAR18", "AYAR14", "KULCEALTIN", "GUMUSTRY",
]);

function resolveHaremPrice(haremCode, karats) {
  if (!haremCode || !karats) return null;
  const direct = karats[haremCode]?.selling;
  if (direct && direct > 0) return direct;
  for (const alt of (HAREM_FALLBACK[haremCode] || [])) {
    const p = karats[alt.code]?.selling;
    if (p && p > 0) return p * (alt.mult || 1);
  }
  return null;
}

function calcPrice(p, rates, globalMarginPct = 0, karats = null) {
  // 1. Manuel fiyat kilidi — dükkan sahibi override etmişse başka hiç bir hesap yapma.
  if (p.manual_price != null && Number(p.manual_price) > 0) {
    return Number(Number(p.manual_price).toFixed(2));
  }
  // 2. Harem canlı fiyat — coin/ziynet için direkt birim fiyat,
  //    gram-bazlı kodlarda (ALTIN/AYAR22/GUMUSTRY vb.) fiyat × gram.
  if (p.harem_code) {
    const resolved = resolveHaremPrice(p.harem_code, karats);
    if (resolved) {
      const isGramBasis = GRAM_BASIS_HAREM_CODES.has(p.harem_code);
      const price = isGramBasis ? resolved * (Number(p.net_weight) || 0) : resolved;
      return Number(price.toFixed(2));
    }
  }
  const rate = rates?.[p.metal_type]?.selling_price || 0;
  const raw = p.net_weight * p.purity * rate;
  const total = (raw + p.workmanship_cost) * (1 + p.margin_rate) * (1 + Number(globalMarginPct || 0) / 100);
  return Number(total.toFixed(2));
}

const empty = {
  barcode: "", name: "", category: "altin", metal_type: "gold",
  // Sayısal alanlar boş başlar — kullanıcı gerçek değer girmediği sürece kaydedilmez.
  purity: "", net_weight: "", workmanship_cost: "", margin_rate: "", stock: "",
  image_url: "",
};

export default function Products() {
  const { rates, karats } = useRates();
  const { user } = useAuth();
  const { t } = useI18n();
  const { profitMargin } = useSubscription();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const canEdit = user?.role === "admin" || user?.role === "kasiyer";
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  const uploadImage = async (file, slot = null) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Resim en fazla 5 MB olabilir");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => {
        const galleryList = Array.isArray(e.image_urls) ? [...e.image_urls] : (e.image_url ? [e.image_url] : []);
        if (slot !== null && slot < galleryList.length) {
          galleryList[slot] = r.data.path;
        } else if (galleryList.length < 5) {
          galleryList.push(r.data.path);
        }
        return { ...e, image_url: galleryList[0] || r.data.path, image_urls: galleryList.slice(0, 5) };
      });
      toast.success("Resim yüklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resim yüklenemedi");
    } finally { setUploading(false); }
  };

  const removeGallerySlot = (idx) => {
    setEditing((e) => {
      const list = Array.isArray(e.image_urls) ? [...e.image_urls] : [];
      list.splice(idx, 1);
      return { ...e, image_urls: list, image_url: list[0] || null };
    });
  };

  // Blob-URL image loader (fetch with Auth header) — cached by path
  const [thumbUrls, setThumbUrls] = useState({});
  useEffect(() => {
    const token = tokenStorage.get("uc_token");
    const missing = items.filter(p => p.image_url && !thumbUrls[p.image_url]);
    if (missing.length === 0) return;
    let alive = true;
    (async () => {
      const next = { ...thumbUrls };
      for (const p of missing) {
        if (!p.image_url.startsWith("milyemsoft/")) continue;  // external URLs left as-is below
        try {
          const resp = await fetch(`${API}/products/image/${p.image_url}`, { headers: { Authorization: `Bearer ${token}` } });
          if (!resp.ok) continue;
          const b = await resp.blob();
          next[p.image_url] = URL.createObjectURL(b);
        } catch {}
      }
      if (alive) setThumbUrls(next);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const thumbSrc = (imgUrl) => {
    if (!imgUrl) return null;
    if (imgUrl.startsWith("http")) return imgUrl;
    return thumbUrls[imgUrl] || null;
  };

  const exportCsv = () => {
    const token = tokenStorage.get("uc_token");
    const url = `${API}/products/export.csv`;
    // authenticated download via fetch → blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `urunler_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
      });
  };

  const downloadTemplate = () => {
    const token = tokenStorage.get("uc_token");
    fetch(`${API}/products/template.xlsx`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Şablon alınamadı");
        return r.blob();
      })
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "milyemsoft_urun_sablonu.xlsx";
        a.click();
        toast.success("Örnek şablon indirildi");
      })
      .catch((e) => toast.error(e.message || "İndirme başarısız"));
  };

  const openManual = (p) => {
    setManualFor(p);
    setManualPrice(p.manual_price != null ? String(p.manual_price) : "");
  };
  const saveManual = async () => {
    if (!manualFor) return;
    try {
      const price = manualPrice === "" ? null : Number(manualPrice);
      await api.post(`/products/${manualFor.id}/manual-price`, { price });
      toast.success(price ? `Manuel fiyat sabitlendi: ${price} ₺` : "Manuel fiyat kaldırıldı, canlı fiyat aktif");
      setManualFor(null);
      setManualPrice("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`+${r.data.added} eklendi · ${r.data.updated} güncellendi${r.data.failed?` · ${r.data.failed} hata`:""}`);
      load();
    } catch (e) { toast.error("İçe aktarma hatası"); }
  };

  const printLabel = (pid) => {
    const token = tokenStorage.get("uc_token");
    fetch(`${API}/products/${pid}/label.pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => window.open(URL.createObjectURL(b), "_blank"));
  };

  const load = async () => {
    const r = await api.get("/products");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  // AI Vision → Products hand-off: prefill a new product form if we came from /vision.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "vision") return;
    try {
      const raw = sessionStorage.getItem("ai_vision_draft");
      if (!raw) return;
      const draft = JSON.parse(raw);
      const barcode = `AI${Date.now().toString().slice(-8)}`;
      setEditing({
        ...empty,
        ...draft,
        barcode,
        metal_type: draft.category === "gumus" ? "silver" : (draft.category === "platin" ? "platinum" : "gold"),
      });
      setOpen(true);
      sessionStorage.removeItem("ai_vision_draft");
      // Clean the URL to avoid re-triggering on refresh.
      window.history.replaceState({}, "", "/products");
    } catch {}
  }, []);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return items.filter((p) => !t || p.name.toLowerCase().includes(t) || p.barcode.includes(t));
  }, [items, q]);

  const openNew = () => { setEditing({ ...empty }); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...p }); setOpen(true); };
  const close = () => { setOpen(false); setEditing(null); };

  const [seeding, setSeeding] = useState(false);
  const [manualFor, setManualFor] = useState(null); // product object being manually priced
  const [manualPrice, setManualPrice] = useState("");
  const seedDefaults = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const r = await api.post("/products/seed-defaults");
      const created = r.data?.created?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      const total = r.data?.total_seed || 0;
      if (created && skipped) toast.success(`✨ ${created} ürün stoğa eklendi · ${skipped} zaten mevcuttu (toplam ${total})`);
      else if (created) toast.success(`✨ Bismillah — ${created} ürün stoğa yüklendi`);
      else toast.info(`Tüm ${total} ürün zaten mevcut · stoğunuz hazır`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Standart ürünler yüklenemedi");
    } finally {
      setSeeding(false);
    }
  };

  // Sayısal alanların doğrulama kuralları — her biri için etiket + minimum + üst sınır (opsiyonel).
  const NUMERIC_RULES = [
    { key: "net_weight",       label: "Net Gram",   min: 0,   minExclusive: true },
    { key: "purity",           label: "Ayar",       min: 0,   max: 1, minExclusive: true },
    { key: "workmanship_cost", label: "İşçilik",    min: 0 },
    { key: "margin_rate",      label: "Kar Oranı",  min: 0,   max: 5 },
    { key: "stock",            label: "Stok Adet",  min: 0,   integer: true },
  ];

  const save = async () => {
    // 1) Sayısal alanları Türkçe formattan güvenli çevir. Geçersiz varsa dur.
    const parsedNumbers = {};
    for (const rule of NUMERIC_RULES) {
      const raw = editing[rule.key];
      const { value, valid } = parseTrNumber(raw);
      if (!valid) {
        toast.error(`${rule.label} geçerli bir sayı olmalı. Örn: 10 veya 10,5 (girilen: "${raw ?? ""}")`);
        return;
      }
      if (rule.minExclusive && value <= rule.min) {
        toast.error(`${rule.label} sıfırdan büyük olmalı.`);
        return;
      }
      if (!rule.minExclusive && rule.min != null && value < rule.min) {
        toast.error(`${rule.label} en az ${rule.min} olmalı.`);
        return;
      }
      if (rule.max != null && value > rule.max) {
        toast.error(`${rule.label} en fazla ${rule.max} olabilir. (Örn Ayar: 0,916)`);
        return;
      }
      if (rule.integer && !Number.isInteger(value)) {
        toast.error(`${rule.label} tam sayı olmalı.`);
        return;
      }
      parsedNumbers[rule.key] = value;
    }

    const { id, created_at, ...rest } = editing;
    const body = { ...rest, ...parsedNumbers };

    try {
      if (id) {
        await api.put(`/products/${id}`, body);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/products", body);
        toast.success("Ürün eklendi");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const del = async (p) => {
    if (!confirm(`"${p.name}" silinsin mi?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="label-caps">Stok Kartları</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Ürünler</h1>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={seedDefaults}
              disabled={seeding}
              className="relative overflow-hidden group border border-[--gold] text-[--gold] px-3 py-2 text-xs inline-flex items-center gap-2 hover:bg-[--gold] hover:text-black transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold tracking-wider uppercase"
              data-testid="seed-defaults-btn"
              title="Ziynet · Sikke · Bilezik · Gram · Döviz — tek tıkla tüm dükkan envanteri Harem Altın fiyatlarına bağlı yüklenir"
            >
              {seeding ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {seeding ? "Yükleniyor…" : "Bismillah · Tüm Dükkanı Yükle"}
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e)=>importCsv(e.target.files?.[0])} data-testid="import-file" />
            <button
              onClick={downloadTemplate}
              className="border border-white/20 text-neutral-200 hover:border-[--gold] hover:text-[--gold] px-3 py-2 text-xs inline-flex items-center gap-2 transition-colors"
              data-testid="template-download-btn"
              title="Milyem ve gramaj hücreleri Sayı formatında sabitlenmiş Excel şablonu"
            >
              <FileText size={12} /> Örnek Şablon
            </button>
            <button onClick={()=>fileRef.current?.click()} className="btn-gold flex items-center gap-2" data-testid="import-csv"><Upload size={12} /> İçe Aktar</button>
            <button onClick={exportCsv} className="btn-gold flex items-center gap-2" data-testid="export-csv"><Download size={12} /> Dışa Aktar</button>
            <button onClick={openNew} className="btn-primary-gold flex items-center gap-2" data-testid="add-product-btn">
              <Plus size={14} /> Yeni Ürün
            </button>
          </div>
        )}
      </div>

      <div className="card-flat p-6">
        <input
          data-testid="product-search"
          placeholder="Barkod veya ürün adı..."
          value={q} onChange={(e)=>setQ(e.target.value)}
          className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]"
        />
      </div>

      <div className="card-flat overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-neutral-500 label-caps border-b border-white/5">
              <th className="text-left px-6 py-4"></th>
              <th className="text-left px-6 py-4">Barkod</th>
              <th className="text-left px-6 py-4">Ürün</th>
              <th className="text-left px-6 py-4">Metal</th>
              <th className="text-right px-6 py-4">Ayar</th>
              <th className="text-right px-6 py-4">Gram</th>
              <th className="text-right px-6 py-4">İşçilik</th>
              <th className="text-right px-6 py-4">Anlık Fiyat</th>
              <th className="text-right px-6 py-4">Stok</th>
              {canEdit && <th className="text-right px-6 py-4">İşlem</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/[.02]" data-testid={`product-row-${p.barcode}`}>
                <td className="px-6 py-3">
                  {thumbSrc(p.image_url) ? (
                    <img src={thumbSrc(p.image_url)} alt="" loading="lazy" decoding="async" className="w-10 h-10 object-cover border border-white/10" />
                  ) : (
                    <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-neutral-700" style={{background:"var(--surface-2)"}}>—</div>
                  )}
                </td>
                <td className="px-6 py-4 font-mono text-neutral-400">{p.barcode}</td>
                <td className="px-6 py-4">{p.name}</td>
                <td className="px-6 py-4 text-neutral-400">{p.metal_type === "gold" ? "Altın" : p.metal_type === "silver" ? "Gümüş" : "Platin"}</td>
                <td className="px-6 py-4 text-right font-mono tabular-nums">{(p.purity * 1000).toFixed(0)}‰</td>
                <td className="px-6 py-4 text-right font-mono tabular-nums">{fmt4(p.net_weight)}</td>
                <td className="px-6 py-4 text-right font-mono tabular-nums">{fmt2(p.workmanship_cost)} ₺</td>
                <td className="px-6 py-4 text-right font-mono tabular-nums" style={{color:"var(--gold)"}} data-testid={`price-${p.barcode}`}>
                  {fmt2(calcPrice(p, rates, profitMargin, karats))} ₺
                  {p.manual_price != null && p.manual_price > 0 && (
                    <div className="text-[9px] font-mono tracking-widest text-amber-400 flex items-center justify-end gap-1 mt-0.5">
                      <Lock size={9} /> MANUEL
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-right font-mono">{p.stock}</td>
                {canEdit && (
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={()=>openManual(p)}
                      className={`mr-3 ${p.manual_price != null && p.manual_price > 0 ? "text-amber-400 hover:text-amber-300" : "text-neutral-400 hover:text-[--gold]"}`}
                      title="Manuel fiyat sabitle / kaldır"
                      data-testid={`manual-${p.barcode}`}
                    >
                      {p.manual_price != null && p.manual_price > 0 ? <Lock size={14} /> : <Unlock size={14} />}
                    </button>
                    <button onClick={()=>printLabel(p.id)} className="text-neutral-400 hover:text-[--gold] mr-3" title="Etiket yazdır" data-testid={`label-${p.barcode}`}><Tag size={14} /></button>
                    <button onClick={()=>openEdit(p)} className="text-neutral-400 hover:text-[--gold] mr-3" data-testid={`edit-${p.barcode}`}><Pencil size={14} /></button>
                    {user?.role === "admin" && (
                      <button onClick={()=>del(p)} className="text-neutral-400 hover:text-red-400" data-testid={`del-${p.barcode}`}><Trash2 size={14} /></button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-12 text-neutral-500">Ürün bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="product-modal">
          <div className="w-full max-w-2xl card-flat p-8" style={{background:"var(--surface)"}}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="label-caps">{editing.id ? "Ürünü Düzenle" : "Yeni Ürün"}</div>
                <div className="font-display text-2xl mt-1">Stok Kartı</div>
              </div>
              <button onClick={close} className="text-neutral-400 hover:text-white" data-testid="close-modal"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["Barkod","barcode","text","product-barcode", null],
                ["Ürün Adı","name","text","product-name", null],
                ["Net Gram","net_weight","tr-number","product-weight", "10 veya 10,5"],
                ["Ayar (0-1) ör 0,916","purity","tr-number","product-purity", "0,916"],
                ["İşçilik (₺)","workmanship_cost","tr-number","product-workmanship", "0 veya 250,50"],
                ["Kar Oranı (0-1)","margin_rate","tr-number","product-margin", "0,15"],
                ["Stok Adet","stock","tr-number","product-stock", "1"],
              ].map(([label, key, type, tid, placeholder]) => {
                const isNumeric = type === "tr-number";
                const rawValue = editing[key] ?? "";
                // Türkçe format önizleme: geçerli sayı ise küçük not göster
                const preview = isNumeric && String(rawValue).trim() !== ""
                  ? parseTrNumber(rawValue)
                  : null;
                return (
                  <div key={key}>
                    <label className="label-caps block mb-2">{label}</label>
                    <input
                      data-testid={tid}
                      type="text"
                      inputMode={isNumeric ? "decimal" : "text"}
                      placeholder={placeholder || ""}
                      value={rawValue}
                      onChange={(e)=>setEditing({...editing, [key]: e.target.value})}
                      className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]"
                    />
                    {isNumeric && preview && (
                      <div className="text-[10px] font-mono mt-1 tabular-nums" data-testid={`${tid}-preview`}>
                        {preview.valid ? (
                          <span className="text-emerald-400">≈ {preview.value.toLocaleString("tr-TR", { maximumFractionDigits: 4 })}</span>
                        ) : (
                          <span className="text-red-400">geçersiz sayı</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="col-span-2">
                <label className="label-caps block mb-2">Ürün Galerisi (5 resme kadar)</label>
                <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e)=>uploadImage(e.target.files?.[0])} data-testid="product-image-file" />
                <div className="grid grid-cols-5 gap-2" data-testid="product-gallery">
                  {[0,1,2,3,4].map((idx) => {
                    const gallery = Array.isArray(editing.image_urls) ? editing.image_urls : (editing.image_url ? [editing.image_url] : []);
                    const src = thumbSrc(gallery[idx]);
                    return (
                      <div key={idx} className="relative aspect-square border border-white/10 flex items-center justify-center overflow-hidden group" style={{background:"var(--surface-2)"}} data-testid={`gallery-slot-${idx}`}>
                        {src ? (
                          <>
                            <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                            <button type="button" onClick={()=>removeGallerySlot(idx)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/70 text-white p-1" title="Kaldır">
                              <X size={12} />
                            </button>
                          </>
                        ) : (
                          <button type="button" onClick={()=>{ if ((editing.image_urls?.length || 0) < 5) imgRef.current?.click(); }} className="w-full h-full flex flex-col items-center justify-center text-neutral-600 hover:text-[--gold] hover:border-[--gold]/40">
                            <ImagePlus size={16} />
                            <span className="text-[9px] mt-1">{idx === 0 ? "Ana" : `+${idx}`}</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-neutral-500 mt-2">İlk resim ürünün ana görseli olur. Sürükle bırakla sıra değiştirmek yerine slotu boşaltıp yeniden yükleyin.</div>
              </div>
              <div>
                <label className="label-caps block mb-2">Metal</label>
                <select
                  data-testid="product-metal"
                  value={editing.metal_type}
                  onChange={(e)=>setEditing({...editing, metal_type: e.target.value})}
                  className="w-full bg-[--surface] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]"
                  style={{background:"var(--surface-2)"}}
                >
                  <option value="gold">Altın</option>
                  <option value="silver">Gümüş</option>
                  <option value="platinum">Platin</option>
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">Kategori</label>
                <select
                  data-testid="product-category"
                  value={editing.category}
                  onChange={(e)=>setEditing({...editing, category: e.target.value})}
                  className="w-full bg-[--surface] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]"
                  style={{background:"var(--surface-2)"}}
                >
                  <option value="altin">Altın</option>
                  <option value="gumus">Gümüş</option>
                  <option value="platin">Platin</option>
                  <option value="pirlanta">Pırlanta</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={close} className="btn-gold" data-testid="cancel-product">İptal</button>
              <button onClick={save} className="btn-primary-gold" data-testid="save-product">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Price modal */}
      {manualFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/70 backdrop-blur-sm"
          onClick={() => setManualFor(null)}
          data-testid="manual-price-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm card-flat p-5 sm:p-6"
            style={{ background: "var(--surface)" }}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="min-w-0">
                <div className="label-caps flex items-center gap-1.5"><Lock size={11} style={{color:"var(--gold)"}} /> Manuel Fiyat</div>
                <div className="font-display text-lg mt-1 truncate">{manualFor.name}</div>
                <div className="label-caps mt-0.5">{manualFor.barcode}</div>
              </div>
              <button onClick={() => setManualFor(null)} className="text-neutral-400 hover:text-white shrink-0" aria-label="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="mb-4">
              <label className="label-caps block mb-2">Sabit Fiyat (₺)</label>
              <input
                type="text"
                inputMode="decimal"
                autoFocus
                value={manualPrice}
                onChange={(e) => setManualPrice(sanitizeDecimal(e.target.value, { maxDecimals: 2 }))}
                onKeyDown={(e) => e.key === "Enter" && saveManual()}
                placeholder="Boş bırakırsanız canlı fiyata döner"
                className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-lg font-mono focus:outline-none focus:border-[--gold]"
                data-testid="manual-price-input"
              />
              <div className="label-caps mt-2 text-neutral-500 leading-relaxed">
                Manuel fiyat aktifken Harem canlı fiyatı ve marj hesabı devre dışı olur — dükkan sahibi bu tutarı sabit satar.
              </div>
            </div>
            <div className="flex gap-2">
              {manualFor.manual_price != null && manualFor.manual_price > 0 && (
                <button
                  onClick={async () => {
                    // Direct API call — bypass setState/saveManual stale closure.
                    try {
                      await api.post(`/products/${manualFor.id}/manual-price`, { price: null });
                      toast.success("Manuel fiyat kaldırıldı, canlı fiyat aktif");
                      setManualFor(null);
                      setManualPrice("");
                      await load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Kaldırılamadı");
                    }
                  }}
                  className="flex-1 border border-white/15 hover:border-red-400 hover:text-red-300 py-2.5 text-xs font-mono uppercase tracking-wider transition"
                  data-testid="manual-price-clear"
                >
                  <Unlock size={11} className="inline mr-1" /> Kaldır
                </button>
              )}
              <button
                onClick={saveManual}
                className="btn-primary-gold flex-1 py-2.5"
                data-testid="manual-price-save"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
